import { FC, Fragment, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { getRules } from 'services/flight';
import { FlightRulesDisplay } from 'ts/flight/interfaces/FlightBookResponse';
import Skeleton from 'react-loading-skeleton';
import LoadingBlock from 'components/shared/LoadingBlock';
import { b2b } from 'constants/generic';
import Tabs from 'components/ui/Tabs/Tabs';

interface RulesContentProps {
  flightId: string;
  tokenKey?: string;
  searchToken?: string;
}

const RulesContent: FC<RulesContentProps> = ({ flightId, tokenKey, searchToken }) => {
  const [t] = useTranslation();
  const [urlParams] = useSearchParams();
  const [rules, setRules] = useState<FlightRulesDisplay[]>();
  const [choosenRuleIndex, setChoosenRuleIndex] = useState(0);
  const scrollableContentRef = useRef<HTMLDivElement>(null);
  const [dynamicMaxHeight, setDynamicMaxHeight] = useState<string>('auto');
  useEffect(() => {
    setRules(undefined);
    (async () => {
      const newRules = await getRules({
        id: flightId,
        searchToken: searchToken || urlParams.get(tokenKey || '') || '',
      });

      if (newRules) {
        setRules(newRules);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (scrollableContentRef.current) {
      const height = scrollableContentRef.current.parentElement?.parentElement?.clientHeight;

      if (height) {
        setDynamicMaxHeight(`${height - 60}px`);
      }
    }
  }, [rules]);

  if (!rules) {
    return (
      <Fragment>
        {b2b && <LoadingBlock />}
        <Tabs
          label={t('rules')}
          items={[...Array(2)].map((_, index) => ({
            value: String(index),
            label: <Skeleton width={220} height={30} />,
          }))}
        />
        <div className="wrapper">
          <div className="penalty" style={{ marginRight: 10 }}>
            <Skeleton height={22} width={120} />
          </div>
          <div className="content">
            <Skeleton height={22} width={220} className="mb-5" />
            <Skeleton height={22} width={230} className="mb-5" />
            <Skeleton height={22} width={220} className="mb-5" />
            <Skeleton height={22} width={230} className="mb-5" />
            <Skeleton height={22} width={220} className="mb-5" />
            <Skeleton height={22} width={230} className="mb-5" />
            <Skeleton height={22} width={220} className="mb-5" />
            <Skeleton height={22} width={230} className="mb-5" />
            <Skeleton height={22} width={220} className="mb-5" />
            <Skeleton height={22} width={230} className="mb-5" />
          </div>
        </div>
      </Fragment>
    );
  }

  return (
    <Fragment>
      {!!rules?.length && (
        <Fragment>
          <Tabs
            label={t('passenger.rule_display')}
            value={String(choosenRuleIndex)}
            onValueChange={(v) => {
              setChoosenRuleIndex(Number(v));
              if (scrollableContentRef.current) {
                scrollableContentRef.current.scrollTop = 0;
              }
            }}
            items={rules.map((rule, index) => ({
              value: String(index),
              label: t('passenger.rule_i', {
                i: index + 1,
                airlineCode: rule.airline.code,
                fareBase: rule.fareBase,
                arrivalCode: rule.arrivalCode,
                departureCode: rule.departureCode,
              }),
            }))}
          />
          <div className="flex-row">
            <div style={{ flex: '0 0 140px' }} className="text-xsm">
              {rules[choosenRuleIndex]?.rules.map((rule) => (
                <div
                  className="btn-lnk text-lh cur-poi text-xsm"
                  key={`nav-${rule.code}`}
                  onClick={() => {
                    const element = document.getElementById(`rule-${rule.code}`);
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                >
                  {rule.code}
                </div>
              ))}
            </div>
            <div
              className="flex-one"
              style={{
                maxHeight: dynamicMaxHeight,
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
              ref={scrollableContentRef}
            >
              {rules[choosenRuleIndex]?.rules.map((rule, index) => (
                <div key={`desc-${rule.code}`} id={`rule-${rule.code}`}>
                  {!!rules[choosenRuleIndex] &&
                    index === 0 &&
                    window.location.host.includes('135a') && (
                      <div>
                        {!!rules[choosenRuleIndex].afterDep && (
                          <div>'afterDep': {rules[choosenRuleIndex].afterDep}</div>
                        )}
                        {!!rules[choosenRuleIndex].afterDepCurrency && (
                          <div>'afterDepCurrency': {rules[choosenRuleIndex].afterDepCurrency}</div>
                        )}
                        {!!rules[choosenRuleIndex].afterDepFee && (
                          <div>'afterDepFee': {rules[choosenRuleIndex].afterDepFee}</div>
                        )}

                        {!!rules[choosenRuleIndex].beforeDep && (
                          <div>'beforeDep': {rules[choosenRuleIndex].beforeDep}</div>
                        )}

                        {!!rules[choosenRuleIndex].beforeDepCurrency && (
                          <div>
                            'beforeDepCurrency': {rules[choosenRuleIndex].beforeDepCurrency}
                          </div>
                        )}

                        {!!rules[choosenRuleIndex].beforeDepFee && (
                          <div>'beforeDepFee': {rules[choosenRuleIndex].beforeDepFee}</div>
                        )}

                        {!!rules[choosenRuleIndex].ticketDesc && (
                          <div>'ticketDesc': {rules[choosenRuleIndex].ticketDesc}</div>
                        )}

                        {!!rules[choosenRuleIndex].refundBeforeDep && (
                          <div>
                            {!!rules[choosenRuleIndex].refundBeforeDep?.desc && (
                              <div>
                                'refundBeforeDep': {rules[choosenRuleIndex].refundBeforeDep?.desc}
                              </div>
                            )}
                            {!!rules[choosenRuleIndex].refundBeforeDep?.descLoc && (
                              <div>
                                'refundBeforeDep.loc':{' '}
                                {rules[choosenRuleIndex].refundBeforeDep?.descLoc}
                              </div>
                            )}
                          </div>
                        )}

                        {!!rules[choosenRuleIndex].refundAfterDep && (
                          <div>
                            {!!rules[choosenRuleIndex].refundAfterDep?.desc && (
                              <div>
                                'refundAfterDep': {rules[choosenRuleIndex].refundAfterDep?.desc}
                              </div>
                            )}
                            {!!rules[choosenRuleIndex].refundAfterDep?.descLoc && (
                              <div>
                                'refundAfterDep.loc':{' '}
                                {rules[choosenRuleIndex].refundAfterDep?.descLoc}
                              </div>
                            )}
                          </div>
                        )}

                        {!!rules[choosenRuleIndex].changeBeforeDep && (
                          <div>
                            {!!rules[choosenRuleIndex].changeBeforeDep?.desc && (
                              <div>
                                'changeBeforeDep': {rules[choosenRuleIndex].changeBeforeDep?.desc}
                              </div>
                            )}
                            {!!rules[choosenRuleIndex].changeBeforeDep?.descLoc && (
                              <div>
                                'changeBeforeDep.loc':{' '}
                                {rules[choosenRuleIndex].changeBeforeDep?.descLoc}
                              </div>
                            )}
                          </div>
                        )}

                        {!!rules[choosenRuleIndex].changeAfterDep && (
                          <div>
                            {!!rules[choosenRuleIndex].changeAfterDep?.desc && (
                              <div>
                                'changeAfterDep': {rules[choosenRuleIndex].changeAfterDep?.desc}
                              </div>
                            )}
                            {!!rules[choosenRuleIndex].changeAfterDep?.descLoc && (
                              <div>
                                'changeAfterDep.loc':{' '}
                                {rules[choosenRuleIndex].changeAfterDep?.descLoc}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  <div className="fl-pair-ticket-ico mt-10 mb-20">{rule.code}</div>
                  <div
                    className="content"
                    dangerouslySetInnerHTML={{
                      __html: rule.desc.replaceAll('\n', '<br>') || '',
                    }}
                  ></div>
                </div>
              ))}
            </div>
          </div>
        </Fragment>
      )}

      {!rules?.length && <div className="text-center text-st mt-30">{t('mo.no_data_found')}</div>}
    </Fragment>
  );
};

export default RulesContent;
